import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { formatPrice } from '@dashboard/services/formatting/price';
import request, { fetcher } from '@dashboard/services/request';

import { confirm } from '@dashboard/lib/confirm';
import { couponKey } from '@dashboard/lib/queryKeys';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';

import Badge from '@dashboard/components/Badge';
import { Box, BoxContent, BoxItem, BoxTitle } from '@dashboard/components/Box';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import CheckoutListItem from '@dashboard/components/CheckoutListItem';
import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import EventListItem from '@dashboard/components/EventListItem';
import Link from '@dashboard/components/Link';
import PaymentItem from '@dashboard/components/PaymentItem';
import PropertyList from '@dashboard/components/PropertyList';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';
import SubscriptionItem from '@dashboard/components/SubscriptionItem';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';

const { REACT_APP_API_URL: apiUrl } = process.env;

const DiscountsCouponsDetail = () => {
  const { token, sellerId, seller, isAdminLoggedInAsSeller } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const history = useHistory();
  const match = useRouteMatch();
  const queryClient = useQueryClient();

  const { data: coupon, isError } = useQuery({
    queryKey: couponKey(match.params.id),
    queryFn: () => fetcher(`/sellers/${sellerId}/coupons/${match.params.id}`, token),
    enabled: !!match.params.id,
  });

  useEffect(() => {
    if (coupon?.seller) {
      setAdminGhostingSellerId(coupon.seller);
    }
  }, [coupon?.seller, setAdminGhostingSellerId]);

  async function deleteCoupon() {
    try {
      const result = await confirm('Delete this coupon?', {
        confirm: 'Delete',
      });

      if (result) {
        const deleted = await request(
          `${apiUrl}/sellers/${sellerId}/coupons/${match.params.id}`,
          token,
          {
            method: 'DELETE',
          },
        );

        if (!deleted) {
          window.alert('Unable to delete coupon. Please try again or contact support.');
        } else {
          queryClient.invalidateQueries({ queryKey: couponKey(match.params.id) });
          history.push(`/coupons`);
        }
      }
    } catch (err) {
      window.alert('Unable to delete coupon. Please try again or contact support');
    }
  }

  if (!isError && !coupon) {
    return (
      <Card variant="root">
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!coupon || isError) {
    return (
      <Card variant="root">
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root">{coupon.label}</CardHeaderTitle>

          {!coupon.deleted && (
            <Box>
              <BoxItem className="sm:ml-auto mt-auto" style={{ paddingLeft: 0 }}>
                <BoxContent>
                  <Button appearance="light" onClick={deleteCoupon}>
                    Delete coupon
                  </Button>
                </BoxContent>
              </BoxItem>
            </Box>
          )}
        </CardHeader>
        <CardContent variant="root" borderBottom>
          <Box>
            <Box style={{ padding: 0 }}>
              <BoxItem>
                <BoxTitle>Value</BoxTitle>
                <BoxContent>
                  {coupon.amountOff
                    ? formatPrice({
                        amount: coupon.amountOff,
                        currency: coupon.currency,
                      })
                    : `${coupon.percentOff}%`}
                </BoxContent>
              </BoxItem>

              <BoxItem>
                <BoxTitle>Coupon code</BoxTitle>
                <BoxContent className="flex">
                  <span className="mr-2 mb-auto">{coupon.code}</span>
                  <CopyToClipboard text={coupon.code}>
                    <button
                      className="text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 w-6 flex items-center justify-center rounded transition-colors"
                      style={{ padding: 0 }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        height="16px"
                        style={{ fill: 'none' }}
                        className="fill-current stroke-current"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                        />
                      </svg>
                    </button>
                  </CopyToClipboard>
                </BoxContent>
              </BoxItem>

              <BoxItem>
                <BoxTitle>Redemptions</BoxTitle>
                <BoxContent>
                  {coupon.timesRedeemed}
                  {' / '}
                  {coupon.maxRedemptions ? coupon.maxRedemptions : '∞'}
                </BoxContent>
              </BoxItem>

              {coupon.stripeId && (
                <BoxItem>
                  <BoxTitle>Stripe coupon</BoxTitle>
                  <BoxContent>
                    <Link
                      to={getStripeDashboardUrl(`/coupons/${coupon.stripeId}`, {
                        seller,
                        isAdminLoggedInAsSeller,
                      })}
                      blank
                      external
                    >
                      {coupon.stripeId}
                    </Link>
                  </BoxContent>
                </BoxItem>
              )}

              <BoxItem>
                <BoxTitle>Status</BoxTitle>
                <BoxContent>
                  <CouponStatusBadge coupon={coupon} />
                </BoxContent>
              </BoxItem>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card className="max-w-lg">
        <CardHeader>
          <CardHeaderTitle>Details</CardHeaderTitle>
        </CardHeader>
        <CardContent>
          {!!coupon.type && (
            <>
              <PropertyList.Item>
                <PropertyList.ItemLabel>Page type</PropertyList.ItemLabel>
                <PropertyList.ItemValue>
                  {coupon.type === 'charge' && 'One-off payments'}

                  {coupon.type === 'subscription' && 'Subscriptions'}

                  <Badge appearance="info" className="ml-2">
                    Legacy
                  </Badge>
                </PropertyList.ItemValue>
              </PropertyList.Item>
              <PropertyList.Item>
                <PropertyList.ItemLabel></PropertyList.ItemLabel>
                <PropertyList.ItemValue>
                  This is a legacy coupon that only works on one-time payment links. To use this
                  coupon for both one-time and subscription payments, please recreate it.
                </PropertyList.ItemValue>
              </PropertyList.Item>
            </>
          )}

          <PropertyList.Item>
            <PropertyList.ItemLabel>Applies to setup fee</PropertyList.ItemLabel>
            <PropertyList.ItemValue>
              {coupon.appliesToSetupFee ? 'Yes' : 'No'}
            </PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>
              Duration{' '}
              <InfoTooltip>
                On subscriptions, the discount will be applied for this duration.
              </InfoTooltip>
            </PropertyList.ItemLabel>
            <PropertyList.ItemValue>
              {coupon.duration === 'forever' ? 'Every payment' : ''}
              {coupon.duration === 'once' ? 'First payment' : ''}
              {coupon.duration === 'repeating'
                ? `Multiple payments (${coupon.durationInMonths} months)`
                : ''}
            </PropertyList.ItemValue>
          </PropertyList.Item>

          <PropertyList.Item>
            <PropertyList.ItemLabel>Valid until</PropertyList.ItemLabel>
            <PropertyList.ItemValue className="capitalize">
              {coupon.redeemBy ? format(new Date(coupon.redeemBy), 'd MMM, yyyy, HH:mm') : '–'}
            </PropertyList.ItemValue>
          </PropertyList.Item>
          <PropertyList.Item>
            <PropertyList.ItemLabel>Max redemptions</PropertyList.ItemLabel>
            <PropertyList.ItemValue className="capitalize">
              {coupon.maxRedemptions ? coupon.maxRedemptions : '–'}
            </PropertyList.ItemValue>
          </PropertyList.Item>
          <PropertyList.Item>
            <PropertyList.ItemLabel>Created at</PropertyList.ItemLabel>
            <PropertyList.ItemValue className="capitalize">
              {format(new Date(coupon.createdAt), 'd MMM, yyyy, HH:mm')}
            </PropertyList.ItemValue>
          </PropertyList.Item>
        </CardContent>
      </Card>

      {coupon.pages && coupon.pages.length > 0 && (
        <Card variant="root">
          <CardHeader variant="default" className="px-4">
            <CardHeaderTitle>Pages this coupon is valid for</CardHeaderTitle>
          </CardHeader>
          <CardContent>
            {coupon.pages.map(page => {
              if (page.type === 'checkout') {
                return <CheckoutListItem key={page._id} checkout={page} showPageTypeLabel />;
              }

              if (page.type === 'event') {
                return <EventListItem key={page._id} page={page} showPageTypeLabel />;
              }

              return null;
            })}
          </CardContent>
        </Card>
      )}

      {coupon.timesRedeemed > 0 && (
        <>
          {coupon.charges && coupon.charges.length > 0 && (
            <Card variant="root" borderBottom>
              <CardHeader className="px-4">
                <CardHeaderTitle>One-time payment redemptions</CardHeaderTitle>
              </CardHeader>
              <CardContent style={{ padding: 0 }}>
                <CardListItem variant="header">
                  <CardListItemElement size="3xl">Customer</CardListItemElement>
                  <CardListItemElement size="large">Amount</CardListItemElement>
                  <CardListItemElement size="4xl">Page</CardListItemElement>
                  <CardListItemElement size="medium" align="right">
                    Type
                  </CardListItemElement>
                  <CardListItemElement size="xl">Date</CardListItemElement>
                  <CardListItemElement size="medium">Status</CardListItemElement>
                </CardListItem>

                <div className="overflow-auto">
                  {coupon.charges.map(payment => (
                    <PaymentItem key={payment._id} payment={payment} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {coupon.subscriptions && coupon.subscriptions.length > 0 && (
            <Card variant="root" borderBottom>
              <CardHeader className="px-4">
                <CardHeaderTitle>Subscription redemptions</CardHeaderTitle>
              </CardHeader>

              <CardContent style={{ padding: 0 }}>
                <CardListItem variant="header">
                  <CardListItemElement size="3xl">Customer</CardListItemElement>
                  <CardListItemElement size="xl">Amount</CardListItemElement>
                  <CardListItemElement size="4xl">Page</CardListItemElement>
                  <CardListItemElement size="xl" align="right">
                    Date
                  </CardListItemElement>
                  <CardListItemElement size="medium">Status</CardListItemElement>
                </CardListItem>

                <div className="overflow-auto">
                  {coupon.subscriptions.map(subscription => (
                    <SubscriptionItem key={subscription._id} subscription={subscription} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </>
  );
};

function CouponStatusBadge({ coupon }) {
  if (coupon.deleted) {
    return <StatusBadge status="deleted">Deleted</StatusBadge>;
  }

  if (coupon.valid) {
    return <StatusBadge status="valid">Valid</StatusBadge>;
  }

  return <StatusBadge status="expired">Expired</StatusBadge>;
}

export default DiscountsCouponsDetail;
